import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { Responsable, ResultadoAsignacion } from '../../negocio/negocio.types';
import { ResponsablesService } from '../services/responsables.service';
import { MOTIVO_MAX, NOMBRE_MAX, fechaCorta, rangoVigencia, validarReasignacion } from '../utils/responsables';
import { mensajeDeError } from '../../negocio/errores';

const CLASE_CAMPO =
  'mt-1 w-full rounded-md border border-solid border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none';

interface Props {
  responsable: Responsable;
  supervisores: string[];
  regionales: string[];
  onHide: () => void;
  /** Sólo cuando SÍ escribió (no en «sin cambio»): la pantalla recarga. */
  onCambio: (r: ResultadoAsignacion) => void;
}

const vacioANull = (v: string) => (v.trim() ? v.trim() : null);

/**
 * Reasignar supervisor y regional de UNA tienda (Consulta CM, fase 1).
 * La fila vigente se cierra el día anterior a «Vigente desde»; la historia no
 * se reescribe (409 VIGENCIA_ANTERIOR). La misma fecha que la vigente la anula.
 */
export const ReasignarDialog: React.FC<Props> = ({ responsable: r, supervisores, regionales, onHide, onCambio }) => {
  const rango = rangoVigencia();
  const [supervisor, setSupervisor] = useState(r.supervisor ?? '');
  const [regional, setRegional] = useState(r.regional ?? '');
  const [vigenteDesde, setVigenteDesde] = useState(rango.hoy);
  const [motivo, setMotivo] = useState('');
  const [errores, setErrores] = useState<string[]>([]);
  const [resultado, setResultado] = useState<ResultadoAsignacion | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setSupervisor(r.supervisor ?? '');
    setRegional(r.regional ?? '');
    setErrores([]);
    setResultado(null);
  }, [r]);

  const guardar = async () => {
    const v = { supervisor: vacioANull(supervisor), regional: vacioANull(regional), vigenteDesde, motivo: motivo.trim() };
    const e = validarReasignacion(v, rango);
    setErrores(e);
    setResultado(null);
    if (e.length) return;
    setOcupado(true);
    try {
      const res = await ResponsablesService.asignar(r.cadena, r.num, {
        supervisor: v.supervisor,
        regional: v.regional,
        vigenteDesde: v.vigenteDesde,
        ...(v.motivo ? { motivo: v.motivo } : {}),
      });
      setResultado(res);
      if (!res.sinCambio) onCambio(res);
    } catch (err) {
      setErrores([mensajeDeError(err)]);
    } finally {
      setOcupado(false);
    }
  };

  const idBase = `reasignar-${r.cadena}-${r.num}`;

  return (
    <Dialog
      header={`Reasignar ${r.cadena} ${r.num} · ${r.tienda}`}
      visible
      onHide={onHide}
      style={{ width: '34rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask={!ocupado}
      footer={
        <div className="flex justify-end gap-2">
          <Button label={resultado ? 'Cerrar' : 'Cancelar'} className="p-button-text" onClick={onHide} disabled={ocupado} />
          <Button label="Guardar asignación" icon="pi pi-check" onClick={() => void guardar()} loading={ocupado} disabled={ocupado} />
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p className="rounded-md border border-solid border-amber-300 bg-amber-50 p-3 text-amber-900">
          ⚠️ Los nombres son llaves de acceso de Metas: cambiarlos mueve quién ve, captura y autoriza esta tienda.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block font-medium" htmlFor={`${idBase}-sup`}>
              Supervisor
            </label>
            <input
              id={`${idBase}-sup`}
              className={CLASE_CAMPO}
              list={`${idBase}-sup-lista`}
              value={supervisor}
              maxLength={NOMBRE_MAX}
              onChange={(e) => setSupervisor(e.target.value)}
              disabled={ocupado}
            />
            <datalist id={`${idBase}-sup-lista`}>
              {supervisores.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="block font-medium" htmlFor={`${idBase}-reg`}>
              Regional
            </label>
            <input
              id={`${idBase}-reg`}
              className={CLASE_CAMPO}
              list={`${idBase}-reg-lista`}
              value={regional}
              maxLength={NOMBRE_MAX}
              onChange={(e) => setRegional(e.target.value)}
              disabled={ocupado}
            />
            <datalist id={`${idBase}-reg-lista`}>
              {regionales.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
        </div>
        <p className="text-xs text-gray-500">Vacío = la tienda queda sin supervisor o sin regional.</p>
        <div>
          <label className="block font-medium" htmlFor={`${idBase}-desde`}>
            Vigente desde
          </label>
          <input
            id={`${idBase}-desde`}
            type="date"
            className={CLASE_CAMPO}
            value={vigenteDesde}
            min={rango.min}
            max={rango.max}
            onChange={(e) => setVigenteDesde(e.target.value)}
            disabled={ocupado}
          />
          <p className="mt-1 text-xs text-gray-500">
            Del {fechaCorta(rango.min)} al {fechaCorta(rango.max)}. La asignación vigente termina el día anterior.
          </p>
        </div>
        <div>
          <label className="block font-medium" htmlFor={`${idBase}-motivo`}>
            Motivo (opcional)
          </label>
          <textarea
            id={`${idBase}-motivo`}
            className={CLASE_CAMPO}
            rows={2}
            maxLength={MOTIVO_MAX}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={ocupado}
          />
        </div>
        {errores.map((e) => (
          <Message key={e} severity="error" text={e} className="w-full justify-start" />
        ))}
        {resultado && (
          <Message
            severity={resultado.sinCambio ? 'info' : 'success'}
            text={resultado.sinCambio ? 'Sin cambio: la tienda ya tenía esos responsables.' : 'Asignación guardada.'}
            className="w-full justify-start"
          />
        )}
        {resultado?.avisos.map((a) => (
          <Message key={a} severity="warn" text={a} className="w-full justify-start" />
        ))}
      </div>
    </Dialog>
  );
};
